/**
 * PLATFORM FEATURE FLAGS
 * ──────────────────────
 * A single Firestore doc — `config/featureFlags` — holding one boolean per
 * in-flight surface. Read once at app boot; superadmin-writable from the CRM.
 *
 * This is deliberately the smallest thing that works. The only prior art in the
 * repo is `PLANNER_DARK` in `src/licensing/licenceApi.js` — a hardcoded constant
 * that needs a rebuild + redeploy to flip. These flags can be toggled live.
 *
 * FAIL CLOSED: if the doc is missing, unreadable, or the read throws, every flag
 * falls back to DEFAULT_FLAGS below. An unreachable Firestore must never
 * accidentally reveal an unfinished surface, so unbuilt features default to
 * `false` and stay that way until someone deliberately turns them on.
 */
import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

export const FEATURE_FLAGS_PATH = ['config', 'featureFlags'];

/** Known flags and their fail-closed defaults. */
export const DEFAULT_FLAGS = {
  // The Author Hub itself. On by default: it is the signed-in landing surface,
  // and the cards degrade gracefully on their own, so there is no half-built
  // state to hide. Set false to fall back to the plain overview dashboard.
  hub: true,
  // Marketing Compass — not built yet. Stays false until the quiz + recommender
  // ship, then flips on for beta testers first (spec §4.7).
  marketingCompass: false,
  // Story Bible (the Plot Planner under its hub display name). False = the card
  // renders "Coming soon". See docs/author-hub-discovery-report.md for why this
  // is not simply "on" — the tool works, but it cannot be bought yet.
  storyBible: false,
};

/** Coerce a raw Firestore doc into a complete, boolean-only flag set. */
export function normaliseFlags(raw) {
  const out = { ...DEFAULT_FLAGS };
  if (!raw || typeof raw !== 'object') return out;
  for (const key of Object.keys(DEFAULT_FLAGS)) {
    if (typeof raw[key] === 'boolean') out[key] = raw[key];
  }
  return out;
}

/**
 * Reads the flag doc once on mount. Returns { flags, loading }.
 *
 * A one-shot getDoc rather than a live subscription: flags change about once a
 * release, and a permanent listener on every dashboard mount is not worth the
 * connection. Callers re-read on reload.
 */
export function useFeatureFlags() {
  const [flags, setFlags] = useState(DEFAULT_FLAGS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, ...FEATURE_FLAGS_PATH));
        if (cancelled) return;
        setFlags(normaliseFlags(snap.exists() ? snap.data() : null));
      } catch (err) {
        // Missing doc, offline, or denied — keep the fail-closed defaults.
        console.warn('[featureFlags] falling back to defaults:', err?.message || err);
        if (!cancelled) setFlags(DEFAULT_FLAGS);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { flags, loading };
}
